import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SeverityBadge } from "@/components/StatusBadge";
import LabChartsGrid from "@/components/LabChartsGrid";
import { LineChart as RLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Sparkles, Loader2, TrendingUp, TrendingDown, Minus, TriangleAlert, LineChart, Activity } from "lucide-react";

function Score({ value }) {
  const v = Math.max(0, Math.min(100, value || 0));
  const color = v >= 70 ? "hsl(var(--success))" : v >= 40 ? "hsl(var(--warning))" : "hsl(var(--destructive))";
  const r = 52, c = 2 * Math.PI * r, off = c - (v / 100) * c;
  return (
    <div className="relative w-32 h-32" data-testid="health-score">
      <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
        <circle cx="60" cy="60" r={r} fill="none" stroke="hsl(var(--border))" strokeWidth="10" />
        <circle cx="60" cy="60" r={r} fill="none" stroke={color} strokeWidth="10"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-mono-data text-3xl font-bold">{v}</span>
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Score</span>
      </div>
    </div>
  );
}

const trendIcon = { melhora: TrendingUp, piora: TrendingDown, estavel: Minus };

export default function InsightsTab({ patientId }) {
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/patients/${patientId}/reports`).then((r) => {
      if (r.data[0]) setReport(r.data[0]);
      setHistory(r.data || []);
    });
  }, [patientId]);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/patients/${patientId}/analyze`);
      setReport(data);
      setHistory((h) => [data, ...h]);
      toast.success("Análise integrada gerada");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const r = report?.result;
  const scoreSeries = [...history]
    .filter((rep) => rep?.result?.health_score != null && rep.created_at)
    .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""))
    .map((rep) => ({ data: (rep.created_at || "").slice(0, 10), score: rep.result.health_score }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-xl font-medium">Análise integrada (IA)</h3>
        <Button onClick={generate} disabled={loading} data-testid="generate-analysis-btn">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {report ? "Regenerar" : "Gerar análise"}
        </Button>
      </div>

      {!r ? (
        <Card className="p-12 text-center border-dashed" data-testid="insights-empty">
          <LineChart className="w-10 h-10 mx-auto mb-4 text-ai opacity-60" />
          <p className="text-muted-foreground max-w-md mx-auto">
            A IA vai cruzar exames laboratoriais, imagens e dados de wearables para gerar um score de
            saúde, organograma de correlações e tendências de melhora.
          </p>
        </Card>
      ) : (
        <div className="space-y-6 animate-fade-up">
          <Card className="p-6 border-l-4 border-ai bg-secondary/50 shadow-none">
            <div className="flex flex-col sm:flex-row gap-6 items-center">
              <Score value={r.health_score} />
              <div className="flex-1">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-ai mb-2">Resumo executivo</p>
                <p className="text-sm leading-relaxed">{r.resumo}</p>
              </div>
            </div>
          </Card>

          {scoreSeries.length >= 2 && (
            <Card className="p-6 border-border shadow-none" data-testid="score-evolution">
              <h4 className="font-heading font-medium mb-4 flex items-center gap-2"><Activity className="w-4 h-4 text-ai" /> Evolução do health score</h4>
              <ResponsiveContainer width="100%" height={200}>
                <RLineChart data={scoreSeries} margin={{ top: 4, right: 12, left: -12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="data" stroke="hsl(var(--muted-foreground))" fontSize={11} interval={0} />
                  <YAxis domain={[0, 100]} stroke="hsl(var(--muted-foreground))" fontSize={11} width={32} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                    formatter={(v) => [`${v}`, "Score"]} />
                  <Line type="monotone" dataKey="score" stroke="hsl(var(--accent-ai))" strokeWidth={2} dot={{ r: 3 }} />
                </RLineChart>
              </ResponsiveContainer>
            </Card>
          )}

          {r.alertas?.length > 0 && (
            <Card className="p-5 border-destructive/30 bg-destructive/5 shadow-none" data-testid="insights-alerts">
              <div className="flex items-center gap-2 text-destructive mb-2">
                <TriangleAlert className="w-4 h-4" />
                <span className="text-xs font-bold uppercase tracking-wider">Alertas</span>
              </div>
              <ul className="list-disc list-inside text-sm space-y-1">
                {r.alertas.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </Card>
          )}

          <Card className="p-6 border-border shadow-none">
            <h4 className="font-heading font-medium mb-4 flex items-center gap-2"><LineChart className="w-4 h-4 text-ai" /> Gráficos de todos os exames laboratoriais</h4>
            <LabChartsGrid patientId={patientId} />
          </Card>

          {r.insights?.length > 0 && (
            <div className="grid md:grid-cols-2 gap-4">
              {r.insights.map((ins, i) => (
                <Card key={i} className="p-5 border-border shadow-none" data-testid={`insight-${i}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h5 className="font-medium">{ins.titulo}</h5>
                    {ins.gravidade && <SeverityBadge severity={ins.gravidade} />}
                  </div>
                  <p className="text-sm text-muted-foreground">{ins.descricao}</p>
                </Card>
              ))}
            </div>
          )}

          {r.melhorias?.length > 0 && (
            <Card className="p-6 border-border shadow-none">
              <h4 className="font-heading font-medium mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-success" /> Tendências</h4>
              <div className="space-y-2">
                {r.melhorias.map((m, i) => {
                  const Icon = trendIcon[m.tendencia] || Minus;
                  const color = m.tendencia === "melhora" ? "text-success" : m.tendencia === "piora" ? "text-destructive" : "text-muted-foreground";
                  return (
                    <div key={i} className="flex items-center gap-3 bg-secondary/50 rounded-md p-3">
                      <Icon className={`w-4 h-4 ${color}`} />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{m.marcador}</p>
                        <p className="text-xs text-muted-foreground">{m.nota}</p>
                      </div>
                      {m.variacao_pct != null && (
                        <span className={`font-mono-data text-sm ${color}`}>
                          {m.variacao_pct > 0 ? "+" : ""}{m.variacao_pct}%
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {r.recomendacoes?.length > 0 && (
            <Card className="p-6 border-border shadow-none">
              <h4 className="font-heading font-medium mb-3">Recomendações</h4>
              <ul className="list-disc list-inside text-sm space-y-1">
                {r.recomendacoes.map((rec, i) => <li key={i}>{rec}</li>)}
              </ul>
            </Card>
          )}

          {r.disclaimer && <p className="text-xs text-muted-foreground italic">{r.disclaimer}</p>}
        </div>
      )}
    </div>
  );
}
